import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { ChatCompose, ChatFeed } from './ChatOverlay';
import { ProductSheet } from './ProductSheet';
import { ProductBagSheet } from './ProductBagSheet';
import { CartModal } from './CartModal';
import { SwipeSheet } from './SwipeSheet';
import { formatMoney, needsVariantPicker, thumbLetter, useCatalogMap, useCatalogProduct } from './catalog';
import { comboPreviewImage, useComboData, useComboPreviewImages } from './combo';
import { IconShare, IconShopBag, IconClose, IconCart, IconVoucher, IconViewer } from './icons';
import { JudgeMeSummary } from './JudgeMeReviews';
import { livePriceDisplay, useProductQuote } from './pricing';
import {
  ChatComment,
  LiveProduct,
  LiveSessionDiscount,
  LiveSessionPublic,
  comboDiscountId,
  findPinnedProduct,
  formatDropClock,
  pickHighlightLiveDrop,
  sameProductId,
} from './utils';
import { YoutubePlayer, YoutubePlayerHandle, sessionYoutubeId } from './players';
import {
  VariantPicker,
  VariantPickerTarget,
} from './VariantPicker';

export type FullLayer = 'live' | 'product' | 'bag' | 'cart';

export type FullPlayerHandle = {
  /** Call synchronously inside a user-gesture handler (iOS muted autoplay). */
  startPlayback: () => void;
  stopPlayback: () => void;
};

const PIN_PERIOD_MS = 15 * 60 * 1000; // 15 minutes
const PIN_PERIOD_SECONDS = PIN_PERIOD_MS / 1000;

/** Deterministic phase so all viewers share the same countdown for a product. */
function pinPhaseMs(productId: string): number {
  let h = 2166136261;
  const s = String(productId);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % PIN_PERIOD_MS;
}

/** Seconds left in the current 15‑min window (device wall clock + product phase). */
function pinSecondsLeft(productId: string, now = Date.now()): number {
  const elapsed = (now + pinPhaseMs(productId)) % PIN_PERIOD_MS;
  return Math.max(1, Math.ceil((PIN_PERIOD_MS - elapsed) / 1000));
}

interface Props {
  apiUrl: string;
  session: LiveSessionPublic;
  active: boolean;
  /** Shared mute preference with mini player (false = sound on). */
  muted: boolean;
  onMutedChange: (muted: boolean) => void;
  cartCount: number;
  viewerCount: number;
  comments: ChatComment[];
  layer: FullLayer;
  selectedProduct: LiveProduct | null;
  productViews: Record<string, number>;
  socialAtc: number;
  onClose: () => void;
  onOpenCart: () => void;
  onOpenProducts: () => void;
  onSelectProduct: (p: LiveProduct) => void;
  onBackFromProduct: () => void;
  onCloseLayer: () => void;
  onAddToCart: (productId: string, variantId?: string) => Promise<void>;
  onBuyNow: (productId: string, variantId?: string) => Promise<void>;
  onAddCombo: (
    productId: string,
    lines: Array<{ variantId: string; quantity: number }>
  ) => Promise<void>;
  onBuyCombo: (
    productId: string,
    lines: Array<{ variantId: string; quantity: number }>
  ) => Promise<void>;
  onSendChat: (message: string, displayName: string) => Promise<void>;
}

/** Keep the LIVE shell sized to the layout viewport (sheet bands / iframe crop). */
function useLayoutViewportShell(ref: RefObject<HTMLElement | null>, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    const apply = () => {
      const layoutH = window.innerHeight;
      const layoutW = Math.min(window.innerWidth, 600);
      el.style.setProperty('--il-layout-height', `${layoutH}px`);
      el.style.setProperty('--il-layout-width', `${layoutW}px`);
      el.style.setProperty('--il-vv-height', `${layoutH}px`);
      el.style.setProperty('--il-visible-height', `${layoutH}px`);
    };

    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [ref, enabled]);
}

function PinTimer({
  productId,
  dropEndsAt,
  durationMinutes,
}: {
  productId: string;
  dropEndsAt?: string | null;
  durationMinutes?: number | null;
}) {
  const dropTotalSeconds =
    durationMinutes != null && durationMinutes > 0 ? durationMinutes * 60 : null;
  const useDrop = Boolean(dropEndsAt && dropTotalSeconds);

  const computeLeft = () => {
    if (useDrop && dropEndsAt) {
      const end = new Date(dropEndsAt).getTime();
      if (!Number.isNaN(end)) return Math.max(0, Math.ceil((end - Date.now()) / 1000));
    }
    return pinSecondsLeft(productId);
  };

  const [left, setLeft] = useState(computeLeft);

  useEffect(() => {
    const tick = () => setLeft(computeLeft());
    tick();
    const t = window.setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [productId, dropEndsAt, durationMinutes]);

  const totalSeconds = useDrop && dropTotalSeconds ? dropTotalSeconds : PIN_PERIOD_SECONDS;
  const label = useDrop
    ? formatDropClock(left * 1000)
    : `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
  return (
    <span className="il-pin-timer">
      <span
        className="il-pin-timer-ring"
        style={{ ['--p' as string]: `${Math.min(100, (left / totalSeconds) * 100)}%` }}
      />
      {label}
    </span>
  );
}

function PinnedCard({
  apiUrl,
  product,
  socialAtc,
  sessionId,
  discounts,
  onOpen,
  onBuy,
  onPickVariant,
}: {
  apiUrl: string;
  product: LiveProduct;
  socialAtc: number;
  sessionId?: string;
  discounts?: LiveSessionDiscount[];
  onOpen: () => void;
  onBuy: (variantId?: string) => void | Promise<void>;
  onPickVariant: (product: LiveProduct, mode: 'buy') => void;
}) {
  const info = useCatalogProduct(product);
  const { data: comboData } = useComboData(apiUrl, comboDiscountId(product));
  const image = info?.image || comboPreviewImage(comboData);
  const title = info?.title || product.item_name || product.item_id;
  const quote = useProductQuote(apiUrl, product, undefined, sessionId);
  const [busy, setBusy] = useState(false);
  const finalPrice =
    quote?.finalTotal != null
      ? quote.finalTotal
      : info?.price != null
        ? Number(info.price)
        : null;
  const compareAt =
    quote?.compareAt != null
      ? quote.compareAt
      : info?.compareAt != null
        ? Number(info.compareAt)
        : quote && quote.originalTotal > quote.finalTotal
          ? quote.originalTotal
          : null;
  const strikes = livePriceDisplay(finalPrice, quote, compareAt);
  const voucherDiscount =
    quote && quote.voucherDiscount > 0 ? quote.voucherDiscount : null;
  const liveDrop = pickHighlightLiveDrop(discounts, product.item_id);
  const dropQty =
    liveDrop?.usage_limit != null && liveDrop.uses_remaining != null
      ? liveDrop.uses_remaining
      : null;
  const dropTimer =
    liveDrop?.duration_minutes != null && liveDrop.drop_ends_at
      ? { endsAt: liveDrop.drop_ends_at, minutes: liveDrop.duration_minutes }
      : null;
  const slashForPct = strikes.compare ?? strikes.preLive;
  const pctOff =
    slashForPct != null && strikes.now != null && slashForPct > strikes.now
      ? Math.round(((slashForPct - strikes.now) / slashForPct) * 100)
      : null;

  const label = (Number(product.sort_order) || 0) + 1;

  return (
    <div className="il-pin-card">
      <button type="button" className="il-pin-main" onClick={onOpen}>
        <span className="il-pin-thumb">
          <span className="il-bag-num" aria-hidden>
            {label}
          </span>
          {image ? (
            <img src={image} alt="" />
          ) : (
            <span className="il-bag-ph">{thumbLetter(title)}</span>
          )}
          <span className="il-pin-tag il-pin-tag-card">
            <span className="il-pin-tag-dot" aria-hidden />
            LIVE
          </span>
          <PinTimer
            productId={product.item_id}
            dropEndsAt={dropTimer?.endsAt}
            durationMinutes={dropTimer?.minutes}
          />
        </span>
        <span className="il-pin-meta">
          <span className="il-pin-name">{title}</span>
          <JudgeMeSummary apiUrl={apiUrl} productId={product.item_id} />
          {quote?.freeShipping && (
            <span className="il-bag-stats">
              <span className="il-stat-ship">Free shipping</span>
            </span>
          )}
          <span className="il-bag-prices">
            {(strikes.preLive != null || strikes.compare != null) && (
              <span className="il-bag-price-top">
                {strikes.preLive != null && (
                  <span className="il-price-was-live">RM {formatMoney(strikes.preLive)}</span>
                )}
                {strikes.compare != null && (
                  <span className="il-price-was-compare">RM {formatMoney(strikes.compare)}</span>
                )}
                {pctOff != null && pctOff > 0 && (
                  <span className="il-bag-pct">{pctOff}% off</span>
                )}
              </span>
            )}
            <span className="il-bag-price-now">
              <span className="il-price">
                {strikes.now != null ? `RM ${formatMoney(strikes.now)}` : ''}
              </span>
              {(voucherDiscount != null || dropQty != null) && (
                <span
                  className="il-price-voucher il-bag-voucher-icon"
                  title={
                    voucherDiscount != null
                      ? `Voucher −RM ${formatMoney(voucherDiscount)}${
                          dropQty != null ? ` · ${dropQty} left` : ''
                        }`
                      : `${dropQty} left`
                  }
                >
                  <IconVoucher size={16} />
                  {dropQty != null && (
                    <span className="il-bag-voucher-qty">{dropQty}</span>
                  )}
                </span>
              )}
              {socialAtc > 0 && (
                <span className="il-pin-social">{socialAtc} buying now</span>
              )}
            </span>
          </span>
        </span>
      </button>
      <button
        type="button"
        className="il-pin-buy"
        disabled={busy}
        onClick={async () => {
          if (busy) return;
          // Combos need the multi-step picker — send the shopper into the PDP
          if (comboDiscountId(product)) {
            onOpen();
            return;
          }
          if (needsVariantPicker(info)) {
            onPickVariant(product, 'buy');
            return;
          }
          try {
            setBusy(true);
            await onBuy();
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? <span className="il-btn-spinner" aria-hidden /> : 'Buy'}
      </button>
    </div>
  );
}

function ListPinCard({
  apiUrl,
  products,
  onOpen,
}: {
  apiUrl: string;
  products: LiveProduct[];
  onOpen: () => void;
}) {
  const catalog = useCatalogMap(products);
  const comboImages = useComboPreviewImages(apiUrl, products);
  const preview = products.slice(0, 3);
  return (
    <button type="button" className="il-list-pin" onClick={onOpen}>
      <span className="il-list-pin-thumbs">
        {preview.map((p) => {
          const info = catalog.get(String(p.item_id));
          const image = info?.image || comboImages.get(String(p.item_id)) || null;
          return (
            <span key={p.item_id} className="il-list-pin-thumb">
              {image ? (
                <img src={image} alt="" />
              ) : (
                <span className="il-bag-ph">{thumbLetter(info?.title || p.item_name)}</span>
              )}
            </span>
          );
        })}
      </span>
      <span className="il-list-pin-meta">
        <span className="il-list-pin-title">Shop LIVE</span>
        <span className="il-list-pin-sub">{products.length} products</span>
      </span>
      <span className="il-pin-buy">View</span>
    </button>
  );
}

export const FullPlayer = forwardRef<FullPlayerHandle, Props>(function FullPlayer(
  {
    apiUrl,
    session,
    active,
    muted,
    onMutedChange,
    cartCount,
    viewerCount,
    comments,
    layer,
    selectedProduct,
    productViews,
    socialAtc,
    onClose,
    onOpenCart,
    onOpenProducts,
    onSelectProduct,
    onBackFromProduct,
    onCloseLayer,
    onAddToCart,
    onBuyNow,
    onAddCombo,
    onBuyCombo,
    onSendChat,
  },
  ref
) {
  const shellRef = useRef<HTMLDivElement>(null);
  const ytRef = useRef<YoutubePlayerHandle>(null);
  const lastShownSocialAtc = useRef<number | null>(null);
  const [showSocialToast, setShowSocialToast] = useState(false);
  const [ytKey, setYtKey] = useState(0);
  const sheetLayer = layer === 'product' || layer === 'bag';
  const [videoCompact, setVideoCompact] = useState(sheetLayer);
  const [composeFocus, setComposeFocus] = useState(false);
  const [variantTarget, setVariantTarget] = useState<VariantPickerTarget | null>(null);
  const [cartBump, setCartBump] = useState(false);
  const [cartBumpKey, setCartBumpKey] = useState(0);
  const cartBumpTimer = useRef<number | null>(null);
  /** Compose focus only — send/emoji + dock chrome; no viewport/keyboard layout. */
  const keyboardUi = layer === 'live' && composeFocus;

  useLayoutViewportShell(shellRef, active);

  useEffect(() => {
    if (sheetLayer) setVideoCompact(true);
    else setVideoCompact(false);
  }, [sheetLayer]);

  useEffect(
    () => () => {
      if (cartBumpTimer.current) window.clearTimeout(cartBumpTimer.current);
    },
    []
  );

  const triggerCartBump = () => {
    if (cartBumpTimer.current) window.clearTimeout(cartBumpTimer.current);
    // Remount bump target so the CSS animation always restarts
    setCartBumpKey((k) => k + 1);
    setCartBump(true);
    cartBumpTimer.current = window.setTimeout(() => setCartBump(false), 700);
  };

  const handleAddToCart = async (productId: string, variantId?: string) => {
    await onAddToCart(productId, variantId);
    triggerCartBump();
  };

  const handleAddCombo = async (
    productId: string,
    lines: Array<{ variantId: string; quantity: number }>
  ) => {
    await onAddCombo(productId, lines);
    triggerCartBump();
  };

  const onSheetExitStart = () => setVideoCompact(false);

  useImperativeHandle(ref, () => ({
    startPlayback: () => {
      const shell = shellRef.current;
      shell?.classList.add('is-open');
      shell?.classList.remove('is-closed');
      shell?.setAttribute('aria-hidden', 'false');
      // Fresh enter: muted so unmute is intentional; mini may keep sound after exit
      onMutedChange(true);
      setYtKey((k) => k + 1);
      queueMicrotask(() => {
        ytRef.current?.mute();
        ytRef.current?.play();
      });
    },
    stopPlayback: () => {
      // Keep shared mute state — mini will resume with sound if unmuted
      setYtKey((k) => k + 1);
    },
  }));

  // Chat-style ATC social proof: show 10s only when the count changes
  useEffect(() => {
    if (!active || socialAtc <= 0) return;
    if (lastShownSocialAtc.current === socialAtc) return;
    lastShownSocialAtc.current = socialAtc;
    setShowSocialToast(true);
    const t = window.setTimeout(() => setShowSocialToast(false), 10_000);
    return () => clearTimeout(t);
  }, [active, socialAtc]);

  const showcase = findPinnedProduct(session);
  const products = session.products || [];
  const youtubeId = sessionYoutubeId(session);
  const hasStream = Boolean(youtubeId);

  const onVideoTap = () => {
    // Same user gesture: try play (helps iOS / paused streams), then mute toggle
    ytRef.current?.play();
    const next = ytRef.current?.toggleMute();
    if (typeof next === 'boolean') onMutedChange(next);
    else onMutedChange(!muted);
    // After unmute, nudge play again while gesture context still applies
    ytRef.current?.play();
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: session.title, url: window.location.href });
      } else {
        await navigator.clipboard?.writeText(window.location.href);
      }
    } catch {
      // ignore
    }
  };

  return (
    <div
      className={`il-full${active ? ' is-open' : ' is-closed'}${
        videoCompact ? ' is-sheet-open' : ''
      }${keyboardUi ? ' is-keyboard-open' : ''}`}
      ref={shellRef}
      aria-hidden={!active}
    >
      <div className="il-full-stage">
        <div className="il-full-video">
          {hasStream && youtubeId ? (
            <>
              <YoutubePlayer
                key={`yt-full-${ytKey}-${youtubeId}`}
                ref={ytRef}
                urlOrId={youtubeId}
                active={active}
                muted={muted}
                autoplay
                controls={false}
                onMutedChange={onMutedChange}
              />
              {/* Tap video: try play + toggle mute — sits under commerce chrome */}
              {active && layer === 'live' && (
                <button
                  type="button"
                  className="il-yt-tap"
                  aria-label={muted ? 'Unmute' : 'Mute'}
                  onClick={onVideoTap}
                />
              )}
              {active && layer === 'live' && muted && (
                <div className="il-yt-mute-badge" role="status">
                  <span className="il-yt-mute-badge-icon" aria-hidden="true" />
                  Muted
                </div>
              )}
            </>
          ) : (
            <div className="il-full-empty">No YouTube stream linked</div>
          )}
        </div>

        {active && (
          <>
            {layer !== 'cart' && (
              <header className="il-topbar">
                <div className="il-top-live" aria-label={`${viewerCount} viewers`}>
                  <span className="il-live-tag">
                    <span className="il-live-tag-dot" aria-hidden />
                    LIVE
                  </span>
                  <span className="il-viewer-chip">
                    <IconViewer size={12} />
                    {formatViewers(viewerCount)}
                  </span>
                </div>
                <div className="il-top-actions">
                  <button
                    type="button"
                    className={`il-top-cart${cartBump ? ' is-bump' : ''}`}
                    onClick={onOpenCart}
                    aria-label={cartCount > 0 ? `Cart, ${cartCount} items` : 'Cart'}
                  >
                    <span key={cartBumpKey} className="il-top-cart-inner">
                      <IconCart size={18} />
                    </span>
                    {cartCount > 0 && (
                      <span
                        key={`badge-${cartBumpKey}`}
                        className={`il-top-cart-badge${cartBump ? ' is-bump' : ''}`}
                      >
                        {cartCount > 99 ? '99+' : cartCount}
                      </span>
                    )}
                  </button>
                  <button type="button" className="il-top-close" onClick={onClose} aria-label="Close">
                    <IconClose size={18} />
                  </button>
                </div>
              </header>
            )}

            {layer === 'live' && (
              <div className="il-bottom-stack">
                <div className="il-chat">
                  {showSocialToast && socialAtc > 0 && (
                    <div className="il-chat-pill il-social-toast" role="status">
                      <span className="il-chat-from">LIVE</span>
                      <span className="il-chat-msg">
                        {socialAtc} people added to cart just now
                      </span>
                    </div>
                  )}
                  <ChatFeed comments={comments} />
                </div>

                {showcase ? (
                  <PinnedCard
                    apiUrl={apiUrl}
                    product={showcase}
                    socialAtc={socialAtc}
                    sessionId={session.id}
                    discounts={session.discounts}
                    onOpen={() => onSelectProduct(showcase)}
                    onBuy={(variantId) => onBuyNow(showcase.item_id, variantId)}
                    onPickVariant={(p) => setVariantTarget({ product: p, mode: 'buy' })}
                  />
                ) : products.length > 0 ? (
                  <ListPinCard apiUrl={apiUrl} products={products} onOpen={onOpenProducts} />
                ) : null}

                <div className="il-dock">
                  <button
                    type="button"
                    className="il-shop-bag"
                    onClick={onOpenProducts}
                    aria-label="Open shop"
                  >
                    <IconShopBag size={44} />
                    {products.length > 0 && (
                      <span className="il-shop-count">{Math.min(products.length, 99)}</span>
                    )}
                  </button>
                  <div className="il-dock-chat">
                    <ChatCompose
                      onSend={onSendChat}
                      onFocusChange={setComposeFocus}
                      keyboardOpen={keyboardUi}
                    />
                  </div>
                  <button type="button" className="il-dock-share" onClick={share} aria-label="Share">
                    <IconShare size={22} />
                  </button>
                </div>
              </div>
            )}

            {layer === 'product' && selectedProduct && (
              <SwipeSheet axis="down" onClose={onBackFromProduct} className="il-swipe-panel-sheet70">
                <ProductSheet
                  apiUrl={apiUrl}
                  product={selectedProduct}
                  discounts={session.discounts || []}
                  products={session.products || []}
                  onSelectProduct={onSelectProduct}
                  liveSessionId={session.id}
                  isLive={Boolean(
                    showcase && sameProductId(showcase.item_id, selectedProduct.item_id)
                  )}
                  views={productViews[selectedProduct.item_id] || 0}
                  onClose={onBackFromProduct}
                  onBack={onBackFromProduct}
                  onAddToCart={handleAddToCart}
                  onBuyNow={onBuyNow}
                  onAddCombo={handleAddCombo}
                  onBuyCombo={onBuyCombo}
                />
              </SwipeSheet>
            )}

            {layer === 'bag' && (
              <SwipeSheet
                axis="down"
                onClose={onCloseLayer}
                onExitStart={onSheetExitStart}
                className="il-swipe-panel-sheet70"
              >
                <ProductBagSheet
                  apiUrl={apiUrl}
                  session={session}
                  productViews={productViews}
                  onClose={onCloseLayer}
                  onSelectProduct={onSelectProduct}
                  onAddToCart={handleAddToCart}
                  onBuyNow={onBuyNow}
                  cartCount={cartCount}
                  cartBump={cartBump}
                  onOpenCart={onOpenCart}
                />
              </SwipeSheet>
            )}

            {layer === 'cart' && (
              <SwipeSheet
                axis="right"
                onClose={onCloseLayer}
                dimClassName="il-overlay-dim il-overlay-cart"
                className="il-cart-swipe-wrap"
              >
                <CartModal apiUrl={apiUrl} liveSessionId={session.id} onClose={onCloseLayer} />
              </SwipeSheet>
            )}

            <VariantPicker
              target={variantTarget}
              onClose={() => setVariantTarget(null)}
              onConfirm={async (productId, variantId, mode) => {
                if (mode === 'buy') await onBuyNow(productId, variantId);
                else await handleAddToCart(productId, variantId);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
});

function formatViewers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.max(0, Math.floor(n)));
}
